import * as yup from 'yup';
import { CountryCode, getCountryCallingCode, isValidPhoneNumber } from 'libphonenumber-js';

export const MESSAGE = {
  REQUIRED: 'Required field',
  NOT_EMAIL: 'Invalid email',
  PASSWORD:
    // eslint-disable-next-line max-len
    'Should contain at least one digit, one lowercase letter, one uppercase letter, one special character, no space, and it must be 8-16 characters long',
  PASSWORDS_SHOULD_MATCH: 'Passwords should match',
  INVALID_PHONE: 'Enter a valid phone number',
  INVALID_EMAIL: 'Invalid email format',
  INVALID_TEXTAREA: (max: number) => `Ensure this field has no more than ${max} characters`,
  INVALID_TEXT_LENGTH: (max: number) => `Ensure this field has no more than ${max} characters`,
  INVALID_URL: 'Invalid url',
  INVALID_AMOUNT: 'The field should have digits only',
  NO_SPECIAL_CHARACTERS: 'This field must not contain special characters',
};

const PASSWORD_REGEX = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{8,16}$/;
export const SPECIAL_CHARACTERS_REGEX = /^[\p{L}\s]+$/u;
const DIGITS_ONLY_REGEX = /(?=.*?\d)^\$?(([1-9]\d{0,2}(,\d{3})*)|\d+)?(\.\d{1,2})?$/;
export const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
export const DOMAIN_REGEX =
  // eslint-disable-next-line no-useless-escape
  /^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:\/\n?\s]+)\.[^\/\n?]+(?:\/[^\/\n?]+)*$/;

/**
 * The phone input keeps the calling code of the selected country in the value even when no number
 * has been typed yet (`US +1`), so such a value is treated as an empty, and therefore valid,
 * optional phone number.
 *
 * @param country - country code selected in the phone input
 * @param number - phone number in the international format
 */
const isEmptyPhoneNumber = (country: string, number: string) => {
  if (!number || number === '+') return true;
  try {
    return number === `+${getCountryCallingCode(country as CountryCode)}`;
  } catch {
    return false;
  }
};

const validatePassword = (value: string) => PASSWORD_REGEX.test(value);
const validateAmount = (value: string) => DIGITS_ONLY_REGEX.test(value);
const validateEmail = (value: string) => EMAIL_REGEX.test(value);

export const validationSchema = {
  // If you want to validate an email, do it on the BE for consistency
  /** @deprecated */
  emailRequired: () =>
    yup.string().required(MESSAGE.REQUIRED).matches(EMAIL_REGEX, MESSAGE.INVALID_EMAIL),
  requiredMix: () => yup.mixed().required(MESSAGE.REQUIRED),
  password: () =>
    yup
      .string()
      .matches(PASSWORD_REGEX, { message: MESSAGE.PASSWORD, excludeEmptyString: true })
      .nullable(true),
  passwordRequired: () =>
    yup.mixed().required(MESSAGE.REQUIRED).test('password', MESSAGE.PASSWORD, validatePassword),
  string: (max = 100) => yup.string().max(max, MESSAGE.INVALID_TEXT_LENGTH(max)),
  stringRequired: (max = 100, message = MESSAGE.REQUIRED) =>
    yup.string().trim().max(max, MESSAGE.INVALID_TEXT_LENGTH(max)).required(message),
  textArea: (max = 1000) => yup.string().max(max, MESSAGE.INVALID_TEXTAREA(max)),
  url: () => yup.string().url(MESSAGE.INVALID_URL).nullable(),
  amount: (max = 1000) =>
    yup
      .string()
      .required(MESSAGE.REQUIRED)
      .matches(/^\d+(\.\d+)?$/, MESSAGE.INVALID_AMOUNT)
      .max(max, MESSAGE.INVALID_TEXT_LENGTH(max)),
  confirmPassword: (field?: string) =>
    yup
      .mixed()
      .required(MESSAGE.REQUIRED)
      .oneOf([yup.ref(field || 'password')], MESSAGE.PASSWORDS_SHOULD_MATCH),
  phoneRequired: () =>
    yup
      .string()
      .required(MESSAGE.REQUIRED)
      .test({
        name: 'phone',
        exclusive: false,
        params: {},
        message: MESSAGE.INVALID_PHONE,
        test(phone) {
          return !!phone && isValidPhoneNumber(phone, this.parent.country);
        },
      }),
  phone: () =>
    yup
      .string()
      .test({
        name: 'phone',
        exclusive: false,
        params: {},
        message: MESSAGE.INVALID_PHONE,
        test(phone) {
          const [country, number] = phone?.split(' ') || [];
          return isEmptyPhoneNumber(country, number)
            ? true
            : isValidPhoneNumber(number, country as CountryCode);
        },
      })
      .nullable()
      .transform((value) => {
        const [country, number] = value?.split(' ') || [];
        return isEmptyPhoneNumber(country, number) ? null : value;
      }),
};
